import firebase from 'firebase/app';
import 'firebase/auth';
import React, { useCallback, useEffect, useState } from 'react';
import { memoNamed } from '../utils/react/memo-named';
import {
  boardCategory,
  BoardCategory,
  boardCategoryWithDefault,
  boardPostWithDefault,
  BoardPost,
} from '../types/board';
import { StringInput } from './common/input';
import {
  Post,
  PostName,
  PostTextarea,
  PostTime,
  PostTitle,
  TagName,
} from './common/styledBoard';
import { userWithDefault } from '../types/user';
import ReactMarkdown from 'react-markdown';
import { TimestampToString } from './common/firebase';
import Button from '@material-ui/core/Button';
import styled from 'styled-components';

const Root = styled.div`
  padding: 5px;
  background-color: #ececec;
  border-radius: 5px;
`;

interface Props {
  isAdmin: boolean;
  category: BoardCategory;
  setCategory: (category: BoardCategory) => void;
  setPostInfoList: (
    fn: (
      postInfoList: { post: BoardPost; docID: string }[]
    ) => { post: BoardPost; docID: string }[]
  ) => void;
  updatePost: () => void;
}

export const BoardSendPost = memoNamed<Props>('BoardSendPost', (props) => {
  const [postProcess, setPostProcess] = useState<string>('none');
  const [postName, setPostName] = useState<string>('');
  const [postTitle, setPostTitle] = useState<string>('');
  const [postCategory, setPostCategory] = useState<BoardCategory>('others');
  const [postText, setPostText] = useState<string>('');

  useEffect(() => {
    const db = firebase.firestore();
    db.collection('Users')
      .where('userID', '==', firebase.auth().currentUser?.uid ?? '')
      .get()
      .then((snapshot) => {
        if (snapshot.size !== 1) return;
        const _user = userWithDefault(snapshot.docs[0].data());
        setPostName(`${_user.sei} ${_user.mei}`);
      });
  }, []);

  const setPostProcessNone = useCallback(() => {
    setPostProcess('none');
  }, []);

  const setPostProcessInputPost = useCallback(() => {
    setPostProcess('inputPost');
  }, []);

  const setPostProcessConfirmPost = useCallback(() => {
    setPostProcess('confirmPost');
  }, []);

  const onChangePostCategory = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement>) => {
      const _value = event.target.value;
      setPostCategory(boardCategoryWithDefault(_value));
    },
    []
  );

  const onChangePostText = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      setPostText(event.target.value);
    },
    []
  );

  const sendPost = useCallback(() => {
    const db = firebase.firestore();
    const post = boardPostWithDefault({
      isAdmin: props.isAdmin,
      userID: firebase.auth().currentUser?.uid ?? '',
      name: postName,
      title: postTitle,
      category: postCategory,
      text: postText,
      postTime: firebase.firestore.Timestamp.fromDate(new Date()),
      isPublic: true,
    });
    db.collection('Board')
      .add(post)
      .then(() => {
        //初期化
        setPostProcess('none');
        setPostText('');
        setPostTitle('');
        setPostCategory('others');
        db.collection('Users')
          .where('userID', '==', firebase.auth().currentUser?.uid ?? '')
          .get()
          .then((snapshot) => {
            if (snapshot.size !== 1) return;
            const _user = userWithDefault(snapshot.docs[0].data());
            setPostName(`${_user.sei} ${_user.mei}`);
          })
          .catch((error) => console.error(error));
        props.setCategory(postCategory);
        props.updatePost();
      })
      .catch((error) => console.error(error));
  }, [props, postName, postTitle, postCategory, postText]);

  return (
    <div>
      {postProcess === 'none' ? (
        <Button
          variant='contained'
          onClick={setPostProcessInputPost}
          color='primary'
        >
          投稿する
        </Button>
      ) : postProcess === 'inputPost' ? (
        <Root>
          <TagName>名前</TagName>
          <StringInput value={postName} onValueChange={setPostName} />
          <br />
          <TagName>タイトル</TagName>
          <StringInput value={postTitle} onValueChange={setPostTitle} />
          <br />
          <TagName>カテゴリー</TagName>
          <select value={postCategory} onChange={onChangePostCategory}>
            {boardCategory.map((u) => {
              if (!props.isAdmin && u.key === 'fromAdmin') return undefined;
              return (
                <option key={u.key} value={u.key}>
                  {u.name}
                </option>
              );
            })}
          </select>
          <br />
          <TagName>投稿内容</TagName>
          <PostTextarea
            value={postText}
            onChange={onChangePostText}
            rows={10}
          />
          <br />
          <Button
            variant='contained'
            onClick={setPostProcessConfirmPost}
            color='primary'
          >
            確認
          </Button>
          <Button variant='contained' onClick={setPostProcessNone}>
            戻る
          </Button>
        </Root>
      ) : postProcess === 'confirmPost' ? (
        <Root>
          <Post>
            <PostTitle>{postTitle}</PostTitle>
            <PostName>by {postName}</PostName>
            <PostTime>
              {TimestampToString(
                firebase.firestore.Timestamp.fromDate(new Date())
              )}
            </PostTime>
            <br />
            <ReactMarkdown source={postText} />
          </Post>
          <br />
          この内容で送信しますか？
          <br />
          <Button variant='contained' onClick={sendPost} color='primary'>
            送信
          </Button>
          <Button variant='contained' onClick={setPostProcessInputPost}>
            戻る
          </Button>
        </Root>
      ) : undefined}
    </div>
  );
});
